import { Component, computed, effect, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FieldTree, FormField } from '@angular/forms/signals';
import { MatSelectModule } from '@angular/material/select';
import { of } from 'rxjs';
import { MceApiService } from '../../../../core/mceapi.client.generated';
import { appOpt } from '../../../../core/mceapi.client.helpers';
import { FormImage, FormImageComponent } from '../../../../shared/components/form-image/form-image.component';
import { ERA6930P5_STANDARD_GUID } from '../../../../shared/constants';
import { isSome, match, none, Option } from '../../../../shared/functions/functional';
import { EditCable } from '../../cable-edit.model';
import { createSystemVoltageDto } from '../../system-voltage/system-voltage.model';
import { AmbientTemperatureComponent } from '../ambient-temperature/ambient-temperature.component';
import { BuriedComponent } from '../buried/buried.component';
import { GroupingComponent } from '../grouping/grouping.component';
import { UserDefinedFactorComponent } from '../user-defined-factor/user-defined-factor.component';


@Component({
  selector: 'app-factors-era6930p5',
  imports: [FormField, MatSelectModule,  UserDefinedFactorComponent,
            AmbientTemperatureComponent, GroupingComponent, BuriedComponent, FormImageComponent],
  templateUrl: './factors-era6930p5.component.html',
  styles: ``,
})
export class FactorsEra6930p5Component {

  readonly form = input.required<FieldTree<EditCable>>();

  private mceApiService = inject(MceApiService);

  primaryMethodsRx = rxResource({
    params: () => ({
      libraryGuid: this.form().selectedCable.libraryGuid().value(),
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      systemVoltageValid: this.form().systemVoltage().valid()
    }),
    stream: ({ params }) =>
      params.libraryGuid !== '' && params.systemVoltageValid
        ? this.mceApiService.v1CableInstallationMethodsPrimary({
          libraryGuid: params.libraryGuid,
          systemVoltage: params.systemVoltage,
          standardGuid: ERA6930P5_STANDARD_GUID
        })
        : of([])
  });
  primaryMethods = computed(() => this.primaryMethodsRx.value() ?? []);

  secondaryMethodsRx = rxResource({
    params: () => ({
      libraryGuid: this.form().selectedCable.libraryGuid().value(),
      primaryGuid: this.form().installation.era6930P5Factors.primaryMethodGuid().value(),
      systemVoltage:  createSystemVoltageDto(this.form().systemVoltage)(),
      systemVoltageValid: this.form().systemVoltage().valid()
    }),
    stream: ({ params }) =>
      params.libraryGuid !== '' && params.primaryGuid !== '' && params.systemVoltageValid
        ? this.mceApiService.v1CableInstallationMethodsSecondary({
          libraryGuid: params.libraryGuid,
          systemVoltage:params.systemVoltage,
          primaryGuid: params.primaryGuid,
          standardGuid: ERA6930P5_STANDARD_GUID
        })
        : of([])
  });
  secondaryMethods = computed(() => this.secondaryMethodsRx.value() ?? []);

  isBuriedRx = rxResource({
    params: () => ({ secondaryMethod: this.form().installation.era6930P5Factors.secondaryMethodGuid().value() }),
    stream: ({ params }) =>
      params.secondaryMethod !== ''
        ? this.mceApiService.v1CableInstallationMethodsIsBuried(params.secondaryMethod)
        : of({ value: false })
  });
  isBuried = computed(() => this.isBuriedRx.value()?.value ?? false);
  setModelIsBuried = effect(() => {
    this.form().installation.era6930P5Factors.isBuried().value.set(this.isBuried());
  });

  installationImage = computed<Option<string>>(() => {
    let imageUrl = this.secondaryMethods().find(m => m.value === this.form().installation.era6930P5Factors.secondaryMethodGuid().value())?.imageUrl;
    return imageUrl
      ? appOpt(imageUrl)
      : none;
  });



  resetPrimaryMethod = effect(() => {
    const primaryMethodGuid = this.form().installation.era6930P5Factors.primaryMethodGuid().value() ?? '';
    const primaryMethods = this.primaryMethods();

    if (this.primaryMethodsRx.isLoading() || primaryMethods.length === 0) return;
    if (!primaryMethods.some(m => m.value === primaryMethodGuid)) {
      const firstPrimaryMethodGuid = primaryMethods[0].value;
      if (firstPrimaryMethodGuid !== undefined) {
        this.form().installation.era6930P5Factors.primaryMethodGuid().value.set(firstPrimaryMethodGuid);
      }
    }
  });

  resetSecondaryMethod = effect(() => {
    const secondaryMethodGuid = this.form().installation.era6930P5Factors.secondaryMethodGuid().value() ?? '';
    const secondaryMethods = this.secondaryMethods();

    if (this.secondaryMethodsRx.isLoading() || secondaryMethods.length === 0) return;
    if (!secondaryMethods.some(m => m.value === secondaryMethodGuid)) {
      const firstSecondaryMethodGuid = secondaryMethods[0].value;
      if (firstSecondaryMethodGuid !== undefined) {
        this.form().installation.era6930P5Factors.secondaryMethodGuid().value.set(firstSecondaryMethodGuid);
      }
    }
  });

  resetBuried = effect(() => {
    const isBuried = this.isBuried();
    if (!isBuried || !this.form().installation.era6930P5Factors.secondaryMethodGuid().dirty() || this.form().installation.era6930P5Factors.isBuried().value() === isBuried) return;
    this.form().installation.era6930P5Factors.isBuried().value.set(isBuried);
  });

  viewState = computed(() => ({
    hasInstallationImage: isSome(this.installationImage()),
    installationImage: ({
      url: match(() => '', (x) => x)(this.installationImage()),
      alt: 'Installation image',
      options: {
        height: 130,
        width: 260
      }
    } as FormImage)
  }));


}
